import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { officeListForwards, officeRetryForward } from "@/lib/office.functions";
import { RefreshCw, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_employee/medewerker/office/doorgestuurd")({
  head: () => ({
    meta: [
      { title: "Doorgestuurd — EVG Office" },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://elvion-group.nl/medewerker/office/doorgestuurd",
      },
    ],
  }),
  component: ForwardsPage,
});

const TARGETS = [
  { id: "all", label: "Alles" },
  { id: "webhook", label: "Serviceplanner" },
  { id: "email", label: "Interne melding" },
  { id: "confirmation", label: "Bevestiging klant" },
];

const STATUSES = [
  { id: "all", label: "Alles" },
  { id: "ok", label: "Gelukt" },
  { id: "failed", label: "Mislukt" },
  { id: "skipped", label: "Overgeslagen" },
];

function StatusBadge({ status }: { status: string }) {
  if (status === "ok")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Gelukt
      </span>
    );
  if (status === "skipped")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
        <MinusCircle className="h-3 w-3" /> Overgeslagen
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-700">
      <XCircle className="h-3 w-3" /> Mislukt
    </span>
  );
}

function euro(cents?: number | null) {
  if (cents == null) return "—";
  return `€ ${(cents / 100).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ForwardsPage() {
  const [target, setTarget] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const list = useServerFn(officeListForwards);
  const retry = useServerFn(officeRetryForward);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["office-forwards", target, status, search],
    queryFn: () => list({ data: { target, status, search } }),
  });

  const retryMut = useMutation({
    mutationFn: (id: string) => retry({ data: { id } }),
    onSuccess: (res: any) => {
      if (res?.ok) toast.success("Opnieuw doorgestuurd");
      else toast.error(res?.reason ?? "Opnieuw doorsturen is niet gelukt");
      qc.invalidateQueries({ queryKey: ["office-forwards"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Er ging iets mis"),
  });

  const rows: any[] = (data as any)?.rows ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Doorgestuurd</h1>
        <p className="text-sm text-slate-500">
          Wat er met elke aanvraag naar de serviceplanner, de interne melding en
          de klantbevestiging is gebeurd.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TARGETS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTarget(t.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${target === t.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
          >
            {t.label}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        {STATUSES.map((s) => (
          <button
            key={s.id}
            onClick={() => setStatus(s.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${status === s.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
          >
            {s.label}
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op naam of e-mail"
          className="ml-auto w-56 rounded-md border border-slate-200 px-3 py-1.5 text-sm"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Klant</th>
              <th className="px-3 py-2">Bestemming</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Offerte</th>
              <th className="px-3 py-2">Melding</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  Laden…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  Nog niets doorgestuurd.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                  {new Date(r.created_at).toLocaleString("nl-NL", {
                    timeZone: "Europe/Amsterdam",
                  })}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{r.name ?? "—"}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                  {r.request_id && (
                    <Link
                      to="/medewerker/office/aanvragen/$id"
                      params={{ id: r.request_id }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Bekijk aanvraag
                    </Link>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {TARGETS.find((t) => t.id === r.target)?.label ?? r.target}
                  {r.attempts > 1 && (
                    <span className="ml-1 text-xs text-slate-400">
                      ({r.attempts} pogingen)
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={r.status} />
                  {r.status_code ? (
                    <div className="mt-1 text-xs text-slate-400">
                      code {r.status_code}
                    </div>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                  {euro(r.estimate_total_cents)}
                </td>
                <td className="max-w-[260px] px-3 py-2 text-xs text-rose-600">
                  {r.error ?? ""}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status !== "ok" && (
                    <button
                      onClick={() => retryMut.mutate(r.id)}
                      disabled={retryMut.isPending}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <RefreshCw className="h-3 w-3" /> Opnieuw
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
